import { NavLink } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Home, Map, Calendar, Users, UserCircle, LogOut, LogIn, Shield, Settings, Menu, X } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useRole } from "@/hooks/useRole";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

export function Navigation() {
  const { user, signOut } = useAuth();
  const { isAdmin } = useRole();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const { data: settings } = useQuery({
    queryKey: ["siteSettings", "navbar_logo"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("site_settings")
        .select("navbar_logo_url")
        .single();
      if (error) throw error;
      return data;
    },
  });
  
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const publicNavItems = [
    { name: "Home", path: "/", icon: Home },
    { name: "Journeys", path: "/journeys", icon: Map },
    { name: "Events", path: "/sessions", icon: Calendar },
    { name: "Coaching", path: "/coaching", icon: Calendar },
    { name: "Community", path: "/community", icon: Users },
  ];
  
  const authNavItems = [
    { name: "Members", path: "/members", icon: UserCircle },
  ];

  return (
    <nav className={`sticky top-0 z-50 w-full border-b border-border transition-all duration-300 ${
      isScrolled 
        ? 'bg-card/80 backdrop-blur-lg shadow-soft' 
        : 'bg-card shadow-sm'
    }`}>
      <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src={settings?.navbar_logo_url || "/src/assets/vitality-awakening-logo.png"} 
              alt="Vitality Awakening Logo" 
              className="h-10 w-10"
              width="40"
              height="40"
              decoding="async"
            />
            <h1 className="text-2xl font-bold text-foreground">Vitality Awakening</h1>
          </div>

          <div className="hidden lg:flex items-center gap-1">
            {publicNavItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === "/"}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    isActive
                      ? "bg-muted text-primary"
                      : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                  }`
                }
              >
                <span className="font-medium">{item.name}</span>
              </NavLink>
            ))}
            
            {user && authNavItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    isActive
                      ? "bg-muted text-primary"
                      : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                  }`
                }
              >
                <span className="font-medium">{item.name}</span>
              </NavLink>
            ))}
            
            {user && (
              <NavLink
                to="/dashboard"
                className={({ isActive }) =>
                  `flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    isActive
                      ? "bg-muted text-primary"
                      : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                  }`
                }
              >
                <span className="font-medium">Dashboard</span>
                {isAdmin && <Shield className="h-3 w-3 fill-current" />}
              </NavLink>
            )}
            
            {user ? (
              <Button variant="ghost" size="sm" onClick={signOut} className="ml-2">
                Sign Out
              </Button>
            ) : (
              <NavLink to="/auth">
                <Button size="sm" className="ml-2 bg-primary hover:bg-primary/90">
                  Sign In
                </Button>
              </NavLink>
            )}
          </div>

          <div className="lg:hidden">
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72">
                <SheetHeader>
                  <SheetTitle className="flex items-center gap-2">
                    <img 
                      src={settings?.navbar_logo_url || "/src/assets/vitality-awakening-logo.png"} 
                      alt="Vitality Awakening Logo" 
                      className="h-8 w-8" 
                    />
                    <span>Menu</span>
                  </SheetTitle>
                </SheetHeader>
                <div className="flex flex-col gap-4 mt-8">
                  {publicNavItems.map((item) => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      end={item.path === "/"}
                      onClick={() => setMobileMenuOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                          isActive
                            ? "bg-muted text-primary"
                            : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                        }`
                      }
                    >
                      <span className="font-medium">{item.name}</span>
                    </NavLink>
                  ))}
                  
                  {user && authNavItems.map((item) => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                          isActive
                            ? "bg-muted text-primary"
                            : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                        }`
                      }
                    >
                      <span className="font-medium">{item.name}</span>
                    </NavLink>
                  ))}
                  
                  {user && (
                    <NavLink
                      to="/dashboard"
                      onClick={() => setMobileMenuOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                          isActive
                            ? "bg-muted text-primary"
                            : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                        }`
                      }
                    >
                      <span className="font-medium">Dashboard</span>
                      {isAdmin && <Shield className="h-3 w-3 fill-current" />}
                    </NavLink>
                  )}
                  
                  <div className="pt-4 border-t border-border">
                    {user ? (
                      <Button 
                        variant="ghost" 
                        className="w-full justify-start gap-3" 
                        onClick={() => {
                          signOut();
                          setMobileMenuOpen(false);
                        }}
                      >
                        <span>Sign Out</span>
                      </Button>
                    ) : (
                      <NavLink to="/auth" onClick={() => setMobileMenuOpen(false)}>
                        <Button className="w-full justify-start gap-3 bg-primary hover:bg-primary/90">
                          <span>Sign In</span>
                        </Button>
                      </NavLink>
                    )}
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
}
